import React, { useCallback, useRef, useState } from "react";

import { cn } from "@/lib/helpers/cn";
import type { Appointment, AppointmentSlot, PatientDetails } from "@/lib/types/schedule";
import { Button2 } from "@/ui2/atoms/Button";
import { getAutomationDataAttr } from "@/utils/automationUtils";

import { Day } from "./Day";
import {
  findFirstAvailableSlot,
  generateWeekStructure,
  getTodaysDate,
  getWeekWithFirstAvailableAppointment,
} from "./helpers";
import { TodaysDateBadge } from "./TodaysDateBadge";
import { WeekNavigation } from "./WeekNavigation";
import { Schedule } from "./Schedule";

type AppointmentTimePickerProps = {
  appointments: Appointment[];
  disableNextArrow?: boolean;
  patientDetails: PatientDetails;
  selectedAppointment: Nullable<AppointmentSlot>;
  setAppointments: React.Dispatch<React.SetStateAction<Appointment[]>>;
  setSelectedAppointment: React.Dispatch<React.SetStateAction<AppointmentSlot>>;
};

const currentWeekStartDate = getTodaysDate().startOf("isoWeek").toDate();

const DEFAULT_DAY_SIZE = 6;

export const AppointmentTimePicker = ({
  appointments,
  disableNextArrow,
  patientDetails,
  selectedAppointment,
  setAppointments,
  setSelectedAppointment,
}: AppointmentTimePickerProps) => {
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() =>
    getWeekWithFirstAvailableAppointment(appointments, currentWeekStartDate),
  );
  const calendarBodyRef = useRef<HTMLTableSectionElement>(null);

  const handleScrollToAvailableSlotElement = useCallback((firstAvailableSlot: AppointmentSlot) => {
    if (!calendarBodyRef.current || !firstAvailableSlot) return;

    /** Find the target Day component by matching the appointment date */
    const targetDayKey = `${firstAvailableSlot.year}-${firstAvailableSlot.month}-${firstAvailableSlot.day}`;
    const targetDayElement = calendarBodyRef.current.querySelector(
      `[data-appointment-key="${targetDayKey}"]`,
    );

    if (!targetDayElement) return;

    const container = calendarBodyRef.current;
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetDayElement.getBoundingClientRect();

    /** Calculate scroll positions to center the target Day
     *  Always scroll to top vertically
     *  Center horizontally
     */
    const scrollLeft =
      container.scrollLeft +
      (targetRect.left - containerRect.left) -
      (containerRect.width - targetRect.width) / 2;

    container.scrollLeft = Math.max(0, scrollLeft);
    container.scrollTop = 0;
  }, []);

  const handleEarliestAvailableClick = useCallback(() => {
    if (!appointments.length) return;

    const earliestWeekStart = getWeekWithFirstAvailableAppointment(
      appointments,
      currentWeekStartDate,
    );

    setCurrentWeekStart(earliestWeekStart);

    const firstAvailableSlot = findFirstAvailableSlot(appointments);

    if (!firstAvailableSlot) return;

    setSelectedAppointment(firstAvailableSlot);

    /** This needs to be wrapped in a timeout to ensure DOM has been updated after state updates  */
    setTimeout(() => {
      handleScrollToAvailableSlotElement(firstAvailableSlot);
    }, 0);
  }, [appointments]);

  const weekDays = generateWeekStructure(currentWeekStart, appointments);
  const daySizes = weekDays.map((day) => (day && day.slots ? day.slots.length : 0));
  const maxDaySize = Math.max(...daySizes) ?? DEFAULT_DAY_SIZE;

  return (
    <Schedule.Container
      className="tw:w-full tw:block"
      data-public
      {...getAutomationDataAttr("schedule-container")}
    >
      <Schedule.Header className="tw:flex-wrap tw:lg:flex-nowrap">
        <TodaysDateBadge />

        <div
          className={cn(
            "tw:order-3 tw:flex tw:justify-center tw:mt-2.5 tw:pt-2.5 tw:relative tw:w-full",
            "tw:lg:order-none tw:lg:mt-0 tw:lg:pt-0 tw:lg:w-auto",
          )}
        >
          <hr className="tw:bg-border-fill-quinary tw:absolute tw:top-0 tw:-left-3 tw:w-[calc(100%+theme(spacing.6))] tw:lg:hidden tw:!m-0 tw:!p-0" />

          <WeekNavigation
            appointments={appointments}
            currentWeekStart={currentWeekStart}
            disableNextArrow={disableNextArrow}
            patientDetails={patientDetails}
            setAppointments={setAppointments}
            setCurrentWeekStart={setCurrentWeekStart}
          />
        </div>

        <Button2
          className="tw:order-2 tw:lg:order-none"
          onPress={handleEarliestAvailableClick}
          size="sm"
          variant="secondary"
          {...getAutomationDataAttr("appointment-time-picker-earliest")}
        >
          Earliest Available
        </Button2>
      </Schedule.Header>

      {weekDays.length > 0 && (
        <Schedule.Body
          className="tw:w-full tw:max-w-full tw:flex tw:lg:grid-cols-7 tw:lg:grid tw:gap-1.5 tw:overflow-auto tw:h-132"
          id="calendar-body"
          ref={calendarBodyRef}
        >
          {weekDays.map((appointment) => (
            <div
              data-appointment-key={`${appointment.year}-${appointment.month}-${appointment.day}`}
              key={`${appointment.month}_${appointment.day}_${appointment.year}`}
            >
              <Day
                appointment={appointment}
                maxDaySize={maxDaySize}
                selectedAppointment={selectedAppointment}
                setSelectedAppointment={setSelectedAppointment}
              />
            </div>
          ))}
        </Schedule.Body>
      )}

      {weekDays.length === 0 && (
        <div className="tw:flex tw:justify-center tw:items-center" id="calendar-body">
          <p className="tw:text-center">No sessions available in this date range.</p>
        </div>
      )}
    </Schedule.Container>
  );
};
